import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { ThemeService } from '../../core/services/theme.service';
import { ApiConfigService, EnvConfig, EnvName, ENV_CONFIGS } from '../../core/services/api-config.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <header class="top-navbar">
      <div class="page-header">
        <div class="breadcrumb">
          <span class="breadcrumb-root">Admin</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="color: var(--text-muted);">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span class="breadcrumb-current">{{ pageTitle }}</span>
        </div>
        <p class="page-subtitle" *ngIf="pageSubtitle">{{ pageSubtitle }}</p>
      </div>

      <div class="navbar-right">

        <!-- ── Environment Switcher ── -->
        <div class="env-switcher" [class.open]="envDropOpen">
          <button
            class="env-pill"
            [style.--env-color]="activeEnv.color"
            (click)="toggleEnvDrop()"
            title="Switch API environment">
            <span class="env-dot"></span>
            <span class="env-name">{{ activeEnv.label }}</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" [style.transform]="envDropOpen ? 'rotate(180deg)' : ''">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          <!-- Dropdown -->
          <div class="env-dropdown" *ngIf="envDropOpen">
            <div class="env-drop-header">API Environment</div>
            <button
              *ngFor="let env of envList"
              class="env-drop-item"
              [class.active]="activeEnv.name === env.name"
              (click)="selectEnv(env.name)">
              <span class="env-drop-dot" [style.background]="env.color"></span>
              <div class="env-drop-info">
                <span class="env-drop-label">{{ env.label }}</span>
                <span class="env-drop-url">{{ env.apiUrl }}</span>
              </div>
              <svg *ngIf="activeEnv.name === env.name" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </button>
            <div class="env-drop-footer">
              <span class="env-drop-tip">Changes apply immediately. Reload if needed.</span>
            </div>
          </div>
        </div>

        <!-- Divider -->
        <div class="nav-divider"></div>

        <!-- Live Status indicator -->
        <div class="live-badge">
          <span class="live-dot"></span>
          <span class="live-label">Live</span>
        </div>

        <!-- Divider -->
        <div class="nav-divider"></div>

        <!-- Theme Toggle -->
        <button
          class="theme-toggle-btn"
          (click)="toggleTheme()"
          [title]="isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
          [attr.aria-label]="isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
          <!-- Sun icon -->
          <svg *ngIf="isDark" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/>
            <line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/>
            <line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
          <!-- Moon icon -->
          <svg *ngIf="!isDark" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
          <span class="theme-label">{{ isDark ? 'Light' : 'Dark' }}</span>
        </button>

        <!-- Divider -->
        <div class="nav-divider"></div>

        <!-- Admin Avatar -->
        <div class="admin-btn">
          <div class="avatar avatar-gold avatar-sm">A</div>
        </div>
      </div>
    </header>

    <!-- Click-outside overlay -->
    <div class="env-overlay" *ngIf="envDropOpen" (click)="closeEnvDrop()"></div>
  `,
  styles: [`
    .top-navbar {
      height: var(--navbar-height, 68px);
      margin-left: var(--sidebar-width, 260px);
      padding: 0 30px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      border-bottom: 1px solid var(--border-color);
      background: var(--navbar-bg);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 90;
    }

    .page-header {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .breadcrumb-root {
      font-size: 11px;
      font-weight: 600;
      color: var(--text-muted);
      letter-spacing: 0.04em;
    }

    .breadcrumb-current {
      font-size: 16px;
      font-weight: 700;
      color: var(--text-primary);
      letter-spacing: -0.01em;
    }

    .page-subtitle {
      font-size: 11.5px;
      color: var(--text-muted);
      margin-left: 1px;
    }

    /* Right side */
    .navbar-right {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    /* ── Environment Switcher ── */
    .env-switcher {
      position: relative;
    }

    .env-pill {
      display: flex;
      align-items: center;
      gap: 7px;
      background: color-mix(in srgb, var(--env-color, #10B981) 10%, transparent);
      border: 1.5px solid color-mix(in srgb, var(--env-color, #10B981) 35%, transparent);
      border-radius: 999px;
      padding: 5px 13px 5px 10px;
      cursor: pointer;
      font-family: var(--font-sans);
      font-size: 11px;
      font-weight: 800;
      color: var(--env-color, #10B981);
      letter-spacing: 0.08em;
      transition: all 0.2s;
      white-space: nowrap;
    }
    .env-pill:hover {
      background: color-mix(in srgb, var(--env-color, #10B981) 18%, transparent);
    }
    .env-pill svg { transition: transform 0.2s; flex-shrink: 0; }

    .env-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--env-color, #10B981);
      box-shadow: 0 0 6px var(--env-color, #10B981);
      animation: envPulse 2.2s infinite;
      flex-shrink: 0;
    }
    @keyframes envPulse {
      0%, 100% { opacity: 0.6; }
      50%       { opacity: 1; }
    }

    .env-name { text-transform: uppercase; }

    /* Dropdown */
    .env-dropdown {
      position: absolute;
      top: calc(100% + 10px);
      right: 0;
      background: var(--surface-2, #1E1E2E);
      border: 1px solid var(--border-color);
      border-radius: 14px;
      width: 300px;
      box-shadow: 0 16px 50px rgba(0,0,0,0.35);
      overflow: hidden;
      z-index: 200;
      animation: envSlideIn 0.18s ease;
    }
    @keyframes envSlideIn {
      from { opacity: 0; transform: translateY(-8px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .env-drop-header {
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--text-muted);
      padding: 12px 16px 8px;
      border-bottom: 1px solid var(--border-color);
    }

    .env-drop-item {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 13px 16px;
      background: none;
      border: none;
      cursor: pointer;
      font-family: var(--font-sans);
      text-align: left;
      transition: background 0.15s;
      border-bottom: 1px solid var(--border-color);
    }
    .env-drop-item:last-of-type { border-bottom: none; }
    .env-drop-item:hover { background: rgba(255,255,255,0.04); }
    .env-drop-item.active { background: rgba(212,175,55,0.06); }

    .env-drop-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .env-drop-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .env-drop-label {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-primary);
      letter-spacing: 0.04em;
    }

    .env-drop-url {
      font-size: 10.5px;
      color: var(--text-muted);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-family: monospace;
    }

    .env-drop-footer {
      padding: 9px 16px;
      background: rgba(0,0,0,0.15);
    }
    .env-drop-tip {
      font-size: 10px;
      color: var(--text-muted);
    }

    /* Click-outside overlay */
    .env-overlay {
      position: fixed;
      inset: 0;
      z-index: 190;
    }

    .live-badge {
      display: flex;
      align-items: center;
      gap: 7px;
      background: rgba(52, 211, 153, 0.08);
      border: 1px solid rgba(52, 211, 153, 0.2);
      padding: 5px 12px;
      border-radius: var(--radius-full);
    }

    .live-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--success);
      box-shadow: 0 0 8px var(--success);
      animation: pulse 2s infinite;
      flex-shrink: 0;
    }

    .live-label {
      font-size: 11px;
      font-weight: 700;
      color: var(--success);
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    .nav-divider {
      width: 1px;
      height: 22px;
      background: var(--border-color);
    }

    /* Theme Toggle */
    .theme-toggle-btn {
      display: flex;
      align-items: center;
      gap: 7px;
      background: var(--theme-toggle-bg);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-full);
      padding: 6px 14px;
      color: var(--text-secondary);
      font-family: var(--font-sans);
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all var(--transition-base);
      white-space: nowrap;
    }

    .theme-toggle-btn:hover {
      border-color: var(--accent-gold);
      color: var(--accent-gold);
      background: var(--accent-gold-glow);
    }

    .theme-label {
      letter-spacing: 0.02em;
    }

    .admin-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 4px;
      border-radius: var(--radius-md);
      transition: background var(--transition-fast);
    }

    .admin-btn:hover {
      background: rgba(255,255,255,0.04);
    }

    @keyframes pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }
  `]
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() pageTitle = 'Dashboard';
  @Input() pageSubtitle = '';

  activeEnv!: EnvConfig;
  envDropOpen = false;
  envList = Object.values(ENV_CONFIGS);

  private sub!: Subscription;

  constructor(
    private themeService: ThemeService,
    private apiConfig: ApiConfigService,
  ) {}

  ngOnInit(): void {
    this.sub = this.apiConfig.env$.subscribe(env => {
      this.activeEnv = env;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get isDark(): boolean {
    return this.themeService.isDark;
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  toggleEnvDrop(): void {
    this.envDropOpen = !this.envDropOpen;
  }

  closeEnvDrop(): void {
    this.envDropOpen = false;
  }

  selectEnv(name: EnvName): void {
    this.apiConfig.switchEnv(name);
    this.envDropOpen = false;
  }
}
